import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import type { PFAStatus } from "~/api/physicalFitnessAssessments";
import { PFA_STATUS } from "~/api/physicalFitnessAssessments";

interface PreparationScaleChartProps {
  percentage: number;
  circleRadius?: number;
  progressWidth?: number;
  status: PFAStatus;
}

const getDegrees = (value: number) => {
  return (value / 100) * 180;
};

const getPercentage = (value: number) => {
  return value / 10;
};

export const PreparationScaleChart = ({
  percentage,
  status,
}: PreparationScaleChartProps) => {
  const { t } = useTranslation();

  const circleRadius = 110;
  const progressWidth = 15;

  const interiorCircleRadius = circleRadius - progressWidth;

  percentage = getPercentage(percentage);

  const description = percentage > 0 ? t(`pfas.${status}`) : t("pfas.score");

  return (
    <div className="grid h-full items-center rounded-xl bg-gradient-to-b from-surface-container-high to-surface-container-low p-6 text-neutral-900 shadow-sm dark:from-surface-container-high-dark dark:to-surface-container-low-dark dark:text-white">
      <div>
        <h2 className="pb-4 text-xl text-neutral-900 dark:text-white">
          {t("patient_detail.preparation_scale")}
        </h2>
      </div>
      <div className="relative col-span-1 flex flex-col items-center rounded-lg">
        <div
          className="relative flex items-center justify-center overflow-hidden bg-white"
          style={{
            width: circleRadius * 2,
            height: circleRadius,
            borderRadius: `${circleRadius}px ${circleRadius}px 0 0`,
          }}
        >
          <div
            className="absolute left-0"
            style={{
              width: circleRadius * 2,
              height: circleRadius,
              top: circleRadius,
            }}
          >
            <div
              className={twMerge(
                "absolute left-0 top-0 rounded-t-none",
                status === PFA_STATUS.EXCELLENT && "bg-success-90",
                status === PFA_STATUS.SATISFACTORY && "bg-info-70",
                status === PFA_STATUS.UNSATISFACTORY && "bg-error-70",
              )}
              style={{
                width: circleRadius * 2,
                height: circleRadius,
                borderRadius: `${circleRadius}px ${circleRadius}px 0 0`,
                top: -circleRadius,
                borderTopLeftRadius: circleRadius,
                borderTopRightRadius: circleRadius,
                transform: `rotate(${getDegrees(percentage) + 180}deg)`,
                transformOrigin: "bottom center",
              }}
            />
          </div>
          <div
            className="absolute flex items-center justify-center overflow-hidden rounded-b-none bg-gradient-to-b dark:from-surface-container-high-dark dark:to-surface-container-low-dark"
            style={{
              width: interiorCircleRadius * 2,
              height: interiorCircleRadius,
              borderRadius: `${interiorCircleRadius}px ${interiorCircleRadius}px 0 0`,
              top: circleRadius - interiorCircleRadius,
            }}
          >
            <div className="pt-12 text-center text-neutral-900 dark:text-white">
              <p>{description}</p>
              <p className="text-xl font-bold">
                {percentage > 0 ? percentage : t("pfas.no_points")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
